/**
 * Agent Fleet - read-only monitoring view of registered LocalAgents.
 *
 * Shows each agent's liveness (online/stale/offline) and, per GPU: ComfyUI
 * process state, VRAM, utilization, temperature, loaded models, and the
 * active job. Data comes from GET /api/agents/list (agents heartbeat every
 * ~30s); refreshed here every 10s.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../config/environment';

interface AgentGpu {
  index: number;
  name?: string;
  vramTotalMB?: number;
  vramUsedMB?: number;
  utilizationPct?: number;
  tempC?: number;
  comfyStatus?: string;
  loadedModels?: string[];
  activeJobId?: string | null;
}

interface AgentRecord {
  agentId: string;
  name: string;
  host: string;
  version: string;
  status: 'online' | 'stale' | 'offline';
  registeredAt: string;
  lastHeartbeat: string;
  maxJobs: number;
  gpus: AgentGpu[];
}

const STATUS_STYLES: Record<AgentRecord['status'], string> = {
  online: 'bg-green-100 text-green-700',
  stale: 'bg-yellow-100 text-yellow-700',
  offline: 'bg-red-100 text-red-700',
};

const COMFY_STATUS_STYLES: Record<string, string> = {
  up: 'text-green-600',
  external: 'text-green-600',
  starting: 'text-yellow-600',
  restarting: 'text-yellow-600',
  down: 'text-red-600',
  stopped: 'text-gray-500',
};

function heartbeatAge(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!iso || Number.isNaN(ms)) return 'never';
  if (ms < 60_000) return `${Math.max(0, Math.round(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  return `${Math.round(ms / 3_600_000)}h ago`;
}

interface AgentFleetProps {
  accessToken: string;
}

export const AgentFleet: React.FC<AgentFleetProps> = ({ accessToken }) => {
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/agents/list`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setAgents(data.agents || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 10_000);
    return () => clearInterval(timer);
  }, [load]);

  if (loading) {
    return <p className="text-body text-gray-500">Loading agent fleet…</p>;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-body text-red-700">
        Could not load agents: {error}
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-6 py-8 text-center">
        <p className="text-body text-gray-600 mb-1">No agents registered yet.</p>
        <p className="text-label text-gray-400">
          LocalAgents running v2.0+ register automatically at startup.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {agents.map((agent) => (
        <div key={agent.agentId} className="rounded-lg border border-gray-200 bg-white">
          {/* Agent header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[agent.status] || STATUS_STYLES.offline}`}>
                {agent.status}
              </span>
              <span className="font-medium text-gray-900">{agent.name}</span>
              <span className="text-label text-gray-400">{agent.agentId}</span>
            </div>
            <div className="text-label text-gray-500">
              {agent.host && <span className="mr-4">host: {agent.host}</span>}
              {agent.version && <span className="mr-4">v{agent.version}</span>}
              <span>heartbeat: {heartbeatAge(agent.lastHeartbeat)}</span>
            </div>
          </div>

          {/* GPU rows */}
          {agent.gpus.length === 0 ? (
            <p className="px-5 py-3 text-label text-gray-400">No GPU inventory reported.</p>
          ) : (
            <table className="w-full text-label">
              <thead>
                <tr className="text-left text-gray-400">
                  <th className="px-5 py-2 font-medium">GPU</th>
                  <th className="px-3 py-2 font-medium">ComfyUI</th>
                  <th className="px-3 py-2 font-medium">VRAM</th>
                  <th className="px-3 py-2 font-medium">Util</th>
                  <th className="px-3 py-2 font-medium">Temp</th>
                  <th className="px-3 py-2 font-medium">Loaded models</th>
                  <th className="px-3 py-2 font-medium">Active job</th>
                </tr>
              </thead>
              <tbody>
                {agent.gpus.map((gpu) => {
                  const vramPct = gpu.vramTotalMB
                    ? Math.round(((gpu.vramUsedMB || 0) / gpu.vramTotalMB) * 100)
                    : null;
                  return (
                    <tr key={gpu.index} className="border-t border-gray-50">
                      <td className="px-5 py-2 text-gray-700">
                        #{gpu.index}{gpu.name ? ` · ${gpu.name}` : ''}
                      </td>
                      <td className={`px-3 py-2 font-medium ${COMFY_STATUS_STYLES[gpu.comfyStatus || ''] || 'text-gray-500'}`}>
                        {gpu.comfyStatus || '–'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">
                        {vramPct !== null ? (
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 rounded bg-gray-100 overflow-hidden">
                              <div
                                className={`h-full rounded ${vramPct > 90 ? 'bg-red-400' : vramPct > 70 ? 'bg-yellow-400' : 'bg-green-400'}`}
                                style={{ width: `${Math.min(100, vramPct)}%` }}
                              />
                            </div>
                            <span>{Math.round((gpu.vramUsedMB || 0) / 1024)}/{Math.round((gpu.vramTotalMB || 0) / 1024)} GB</span>
                          </div>
                        ) : '–'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{gpu.utilizationPct !== undefined ? `${gpu.utilizationPct}%` : '–'}</td>
                      <td className="px-3 py-2 text-gray-600">{gpu.tempC !== undefined ? `${gpu.tempC}°C` : '–'}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={(gpu.loadedModels || []).join(', ')}>
                        {(gpu.loadedModels || []).length > 0 ? (gpu.loadedModels || []).join(', ') : '–'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 font-mono text-xs">{gpu.activeJobId || '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
};

export default AgentFleet;
